import React from "react";
import { MAX_MISTAKES } from "../../lib/constants";
import { PuzzleDataContext } from "../PuzzleData";
import { format } from "date-fns";
import {
    loadGameStateFromLocalStorage,
    saveGameStateToLocalStorage,
} from "../../lib/local-storage";
import {
    isGameDataEquivalent,
    isGuessesFromGame,
} from "../../lib/game-helpers";
export const GameStatusContext = React.createContext();

function GameStatusProvider({ children }) {
    const { gameData, puzzleId, puzzleDate, sport, isLoading } =
        React.useContext(PuzzleDataContext);
    const [submittedGuesses, setSubmittedGuesses] = React.useState([]);
    const [solvedGameData, setSolvedGameData] = React.useState([]);

    const [isGameOver, setIsGameOver] = React.useState(false);
    const [isGameWon, setIsGameWon] = React.useState(false);
    const [guessCandidate, setGuessCandidate] = React.useState([]);

    const normalizedDate =
        puzzleDate instanceof Date && !Number.isNaN(puzzleDate.valueOf())
            ? format(puzzleDate, "yyyy-MM-dd")
            : "unknown-date";
    const storageKey = `puzzle-${sport}-${puzzleId || normalizedDate}`;

    // Track previous storageKey to detect puzzle changes (sport or date)
    const prevStorageKeyRef = React.useRef(storageKey);

    // Reset all state immediately when switching to a different puzzle
    React.useEffect(() => {
        if (prevStorageKeyRef.current !== storageKey) {
            setSubmittedGuesses([]);
            setSolvedGameData([]);
            setGuessCandidate([]);
            setIsGameOver(false);
            setIsGameWon(false);
            prevStorageKeyRef.current = storageKey;
        }
    }, [storageKey]);

    // Load saved state for the current puzzle
    React.useEffect(() => {
        if (isLoading || !gameData.length) {
            return;
        }
        const loadedState = loadGameStateFromLocalStorage(storageKey);
        if (!isGameDataEquivalent({ gd1: gameData, gd2: loadedState?.gameData })) {
            setSubmittedGuesses([]);
            setSolvedGameData([]);
            setGuessCandidate([]);
            setIsGameOver(false);
            setIsGameWon(false);
            return;
        }
        if (
            !isGuessesFromGame({
                gameData,
                submittedGuesses: loadedState?.submittedGuesses,
            })
        ) {
            return;
        }
        if (Array.isArray(loadedState?.submittedGuesses)) {
            setSubmittedGuesses(loadedState.submittedGuesses);
        }

        if (Array.isArray(loadedState?.solvedGameData)) {
            setSolvedGameData(loadedState.solvedGameData);
        }
    }, [storageKey, isLoading, gameData]);

    const numMistakesUsed = submittedGuesses.length - solvedGameData.length;

    // use effect to check if game is won
    React.useEffect(() => {
        if (solvedGameData.length === gameData.length && gameData.length > 0) {
            setIsGameOver(true);
            setIsGameWon(true);
        }
        const gameState = { submittedGuesses, solvedGameData, gameData };
        saveGameStateToLocalStorage(gameState, storageKey);
    }, [solvedGameData, gameData, storageKey, submittedGuesses]);

    // use effect to check if all mistakes have been used and end the game accordingly
    React.useEffect(() => {
        if (numMistakesUsed >= MAX_MISTAKES) {
            setIsGameOver(true);
            setIsGameWon(false);
        }
        const gameState = { submittedGuesses, solvedGameData, gameData };
        saveGameStateToLocalStorage(gameState, storageKey);
    }, [submittedGuesses, numMistakesUsed, storageKey, gameData, solvedGameData]);

    return (
        <GameStatusContext.Provider
            value={{
                isGameOver,
                isGameWon,
                numMistakesUsed,
                solvedGameData,
                setSolvedGameData,
                submittedGuesses,
                setSubmittedGuesses,
                guessCandidate,
                setGuessCandidate,
            }}
        >
            {children}
        </GameStatusContext.Provider>
    );
}

export default GameStatusProvider;
